import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import ParticlesBackground from '../components/ParticlesBackground';
import MobileMockup from '../components/MobileMockup';
import marketDataService from '../services/marketDataService';
import { 
  ChartBarIcon, 
  ShieldCheckIcon, 
  CurrencyDollarIcon,
  GlobeAltIcon,
  ClockIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon,
  ChevronRightIcon,
  PlayIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  AcademicCapIcon,
  TrophyIcon,
  CheckBadgeIcon,
  RocketLaunchIcon,
  BanknotesIcon,
  ChartPieIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FireIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CpuChipIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

// Import images and video
import heroImage from '../assets/business-person-futuristic-business-environment.jpg';
import heroVideo from '../assets/6710903b7e9e9d521cf7f597-sf2m6mr5ng__c2cef81ea7577bfa6ab98cd8f635cba8__P360.mp4';
import teamImage from '../assets/diverse-group-business-professionals-posing-together-monitoring-room_482257-94131.jpg_kxystx.jpg';
import tradingImage from '../assets/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg';
import analysisImage from '../assets/diverse-teammates-examining-statistics-infographics-computer_482257-90942.jpg_z2rlyq.jpg';
import businessAnalystImage from '../assets/business-analyst-looking-into-statistics-reports-detect-any-obstacles_482257-110400.jpg_qxkemx.jpg';
import teamInvestorsImage from '../assets/team-investors-analyzing-profit-amount-making-decisions_482257-88610.jpg_jptdn5.jpg';
import tabletOverlayImage from '../assets/tablet-business-overlay.jpg';
import investmentImage from '../assets/invest-investment-financial-income-profit-costs-concept_53876-128112.jpg_ilkdzr.jpg';
import bitcoinImage from '../assets/businesswoman-bitcoin.jpg';
import manTabletImage from '../assets/man-is-holding-tablet-with-graph-him_479259-14595.jpg_dz4zsm.jpg';
import analystTeamImage from '../assets/analyst-team-utilize-bi-fintech-analyze-financial-data-ornamented-meeting_31965-377645.jpg_hqqshc.jpg';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPlatformTab, setCurrentPlatformTab] = useState(0);
  const [activeMarketTab, setActiveMarketTab] = useState('crypto');
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, 100]);
  
  // Live market data state
  const [liveMarketData, setLiveMarketData] = useState({
    crypto: [],
    stocks: [],
    indices: []
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch live market data
  const fetchMarketData = async () => {
    setIsLoading(true);
    try {
      const [cryptoData, stockPrices, indices] = await Promise.all([
        marketDataService.getCryptoPrices(),
        marketDataService.getStockPrices(),
        marketDataService.getMarketIndices()
      ]);

      setLiveMarketData({
        crypto: cryptoData || [],
        stocks: stockPrices || [],
        indices: indices || []
      });
    } catch (error) {
      console.error('Error fetching market data:', error);
      setLiveMarketData({
        crypto: [],
        stocks: [],
        indices: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch and periodic updates
  useEffect(() => {
    fetchMarketData();
    
    // Update every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const [marketData, setMarketData] = useState([
    { symbol: 'S&P 500', price: '4,567.89', change: '+1.23%', positive: true },
    { symbol: 'NASDAQ', price: '14,321.45', change: '+0.87%', positive: true },
    { symbol: 'BTC/USD', price: '45,678.90', change: '-2.45%', positive: false },
    { symbol: 'ETH/USD', price: '3,234.56', change: '+3.21%', positive: true },
    { symbol: 'GOLD', price: '1,987.45', change: '+0.62%', positive: true },
    { symbol: 'EUR/USD', price: '1.0856', change: '+0.21%', positive: true },
  ]);

  // Advanced features showcase
  const advancedFeatures = [
    {
      icon: BoltIcon,
      title: 'Lightning-Fast Execution',
      description: 'Execute trades in milliseconds with our cutting-edge infrastructure and direct market access.',
      stats: '< 10ms',
      color: 'bg-yellow-500'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Military-Grade Security',
      description: 'Your assets are protected by the same encryption technology used by global financial institutions.',
      stats: '256-bit SSL',
      color: 'bg-green-500'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Market Access',
      description: 'Trade across 50+ exchanges worldwide with unified portfolio management and real-time data.',
      stats: '50+ Exchanges',
      color: 'bg-blue-500'
    },
    {
      icon: ChartBarIcon,
      title: 'AI-Powered Analytics',
      description: 'Advanced machine learning algorithms provide personalized insights and market predictions.',
      stats: '99.7% Accuracy',
      color: 'bg-purple-500'
    }
  ];

  // Platform capabilities
  const platformTabs = [
    {
      title: 'Advanced Trading',
      content: {
        title: 'Professional Trading Platform',
        description: 'Experience institutional-grade trading with advanced order types, algorithmic trading, and real-time analytics.',
        features: [
          'Real-time Level II market data',
          'Advanced charting with 100+ indicators',
          'Algorithmic trading strategies',
          'Risk management tools',
          'Multi-asset portfolio management',
          'Custom dashboard layouts'
        ],
        image: tradingImage
      }
    },
    {
      title: 'Mobile Trading',
      content: {
        title: 'Trade Anywhere, Anytime',
        description: 'Full-featured mobile app with all desktop capabilities, optimized for on-the-go trading.',
        features: [
          'Native iOS and Android apps',
          'Biometric authentication',
          'Push notifications for price alerts',
          'Offline chart analysis',
          'One-tap trading',
          'Synchronized across all devices'
        ],
        image: analysisImage
      }
    },
    {
      title: 'API Integration',
      content: {
        title: 'Developer-Friendly APIs',
        description: 'Build custom trading applications with our comprehensive REST and WebSocket APIs.',
        features: [
          'RESTful API endpoints',
          'Real-time WebSocket feeds',
          'Python, JavaScript SDKs',
          'Sandbox environment',
          'Rate limiting: 1000 req/sec',
          '99.9% uptime SLA'
        ],
        image: teamImage
      }
    }
  ];

  const features = [
    {
      icon: ChartBarIcon,
      title: 'Advanced Trading Tools',
      description: 'Professional-grade charts, technical indicators, and real-time market analysis powered by institutional-quality data feeds.',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Bank-Level Security',
      description: 'Your investments and personal data are protected with military-grade encryption, cold storage, and multi-factor authentication.',
      gradient: 'from-green-500 to-green-600'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Ultra-Low Fees',
      description: 'Industry-leading low commission rates and transparent pricing with no hidden fees. Trade more, pay less.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Markets',
      description: 'Access to stocks, crypto, forex, commodities, and derivatives from markets around the world, all in one unified platform.',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      icon: ClockIcon,
      title: '24/7 Trading',
      description: 'Trade cryptocurrencies 24/7 and access extended-hours stock trading to never miss an opportunity.',
      gradient: 'from-indigo-500 to-indigo-600'
    },
    {
      icon: UserGroupIcon,
      title: 'Expert Support',
      description: 'Dedicated relationship managers, 24/7 technical support, and access to market experts and research analysts.',
      gradient: 'from-pink-500 to-pink-600'
    },
    {
      icon: AcademicCapIcon,
      title: 'Educational Resources',
      description: 'Comprehensive learning center with webinars, tutorials, market analysis, and trading courses for all skill levels.',
      gradient: 'from-red-500 to-red-600'
    },
    {
      icon: RocketLaunchIcon,
      title: 'Algorithmic Trading',
      description: 'Build and deploy automated trading strategies with our sophisticated algorithmic trading platform.',
      gradient: 'from-cyan-500 to-cyan-600'
    },
    {
      icon: ChartPieIcon,
      title: 'Portfolio Analytics',
      description: 'Advanced portfolio analysis, risk metrics, performance attribution, and tax optimization tools.',
      gradient: 'from-orange-500 to-orange-600'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Professional Day Trader',
      company: 'Quantitative Capital',
      content: 'The execution speed is phenomenal. I\'ve increased my trading efficiency by 300% and my profits by 40% since switching to Crest Edge.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      stats: '+300% Efficiency'
    },
    {
      name: 'Michael Rodriguez',
      role: 'Portfolio Manager',
      company: 'Global Asset Management',
      content: 'The research tools and AI-powered analytics are game-changing. It\'s like having a Bloomberg terminal with better UX.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      stats: '$50M+ Managed'
    },
    {
      name: 'Emily Thompson',
      role: 'Crypto Hedge Fund Manager',
      company: 'Digital Assets Fund',
      content: 'Finally, a platform that takes security seriously while providing institutional-grade trading capabilities.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      stats: '2,000+ BTC Traded'
    },
    {
      name: 'David Kim',
      role: 'Algorithmic Trader',
      company: 'Quantum Trading Systems',
      content: 'The API is robust and the execution is lightning-fast. Perfect for high-frequency trading strategies.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      stats: '1M+ Trades/Month'
    }
  ];

  const stats = [
    { label: 'Active Traders', value: '250K+', description: 'Professional traders worldwide' },
    { label: 'Daily Volume', value: '$12.5B+', description: 'Average daily trading volume' },
    { label: 'Assets Available', value: '10,000+', description: 'Stocks, crypto, forex & more' },
    { label: 'Uptime', value: '99.99%', description: 'Platform reliability guarantee' },
    { label: 'Countries', value: '190+', description: 'Global market coverage' },
    { label: 'Support Response', value: '< 30s', description: 'Average response time' }
  ];

  const awards = [
    { title: 'Best Trading Platform 2024', organization: 'FinTech Awards' },
    { title: 'Most Innovative Broker', organization: 'Trading Excellence' },
    { title: 'Top Security Standards', organization: 'CyberSec Institute' },
    { title: 'Best Customer Service', organization: 'Broker Review' }
  ];

  const tradingAssets = [
    { 
      category: 'Stocks', 
      count: '5,000+', 
      description: 'US, European, Asian equities',
      icon: '📈',
      features: ['Real-time quotes', 'After-hours trading', 'Fractional shares']
    },
    { 
      category: 'Cryptocurrencies', 
      count: '200+', 
      description: 'Major and altcoins',
      icon: '₿',
      features: ['24/7 trading', 'DeFi tokens', 'Staking rewards']
    },
    { 
      category: 'Forex', 
      count: '80+', 
      description: 'Major and exotic pairs',
      icon: '💱',
      features: ['Tight spreads', 'High leverage', 'Expert analysis']
    },
    { 
      category: 'Commodities', 
      count: '50+', 
      description: 'Metals, energy, agriculture',
      icon: '🥇',
      features: ['Futures contracts', 'Physical delivery', 'Hedging tools']
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const platformInterval = setInterval(() => {
      setCurrentPlatformTab((prev) => (prev + 1) % platformTabs.length);
    }, 8000);
    return () => clearInterval(platformInterval);
  }, [platformTabs.length]);

  // Update market data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setMarketData(prev => prev.map(item => ({
        ...item,
        change: `${Math.random() > 0.5 ? '+' : '-'}${(Math.random() * 3).toFixed(2)}%`,
        positive: Math.random() > 0.5
      })));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      {/* Clean Professional Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black">
        {/* Clean Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)`
          }}></div>
        </div>

        {/* Main Content Container */}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-6rem)]">
            
            {/* Left Column - Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              {/* Company Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 bg-blue-600/10 border border-blue-500/20 rounded-full"
              >
                <div className="w-2 h-2 bg-blue-400 rounded-full mr-2"></div>
                <span className="text-blue-300 text-sm font-medium">CREST EDGE BROKERAGE</span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white leading-tight"
              >
                Professional Trading
                <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Made Simple
                </span>
              </motion.h1>
              
              {/* Subtitle */}
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-xl"
              >
                Advanced trading platform with institutional-grade tools, 
                real-time market data, and lightning-fast execution for 
                serious traders and investors.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-row gap-4"
              >
                <Link 
                  to="/register" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Register
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Link>
                <Link 
                  to="/login"
                  className="inline-flex items-center justify-center px-8 py-4 bg-transparent hover:bg-slate-800/50 text-slate-300 hover:text-white font-semibold rounded-lg border border-slate-600 hover:border-slate-500 transition-all duration-200"
                >
                  <PlayIcon className="mr-2 h-4 w-4" />
                  Login
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Column - Video */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              {/* Video Container */}
              <div className="relative bg-slate-800/50 rounded-2xl overflow-hidden border border-slate-700/50 shadow-2xl">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-auto rounded-2xl"
                  style={{ aspectRatio: '16/9' }}
                >
                  <source src={heroVideo} type="video/mp4" />
                </video>
                
                {/* Video Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                    <PlayIcon className="h-8 w-8 text-white ml-1" />
                  </div>
                </div>
              </div>

              {/* Video Stats */}
              <div className="absolute -bottom-6 -right-6 bg-slate-900/90 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">24/7</div>
                  <div className="text-slate-400 text-sm">Live Trading</div>
                </div>
              </div>

              <div className="absolute -top-6 -left-6 bg-slate-900/90 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 shadow-xl">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400">99.9%</div>
                  <div className="text-slate-400 text-sm">Uptime</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Showcase */}
      <section className="py-24 bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Professional Trading Platform
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Experience the power of institutional-grade trading technology designed for the modern trader.
            </p>
          </motion.div>

          {/* Platform Tabs */}
          <div className="flex justify-center mb-12">
            <div className="bg-slate-800/50 rounded-2xl p-2 backdrop-blur-sm border border-slate-700/50">
              {platformTabs.map((tab, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentPlatformTab(index)}
                  className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 ${
                    currentPlatformTab === index
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>
          </div>

          {/* Platform Content */}
          <motion.div
            key={currentPlatformTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-3xl font-bold text-white mb-4">
                  {platformTabs[currentPlatformTab].content.title}
                </h3>
                <p className="text-xl text-slate-300 leading-relaxed">
                  {platformTabs[currentPlatformTab].content.description}
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {platformTabs[currentPlatformTab].content.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex space-x-4">
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Get Started
                </Link>
                <Link
                  to="/trading-platform"
                  className="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Learn More
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={platformTabs[currentPlatformTab].content.image}
                  alt={platformTabs[currentPlatformTab].content.title}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Copy Trading Section - Clean White Design */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
              <span className="text-blue-600 text-sm font-medium">COPY TRADING</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Follow Elite Traders
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Copy Their Success
              </span>
            </h2>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Automatically mirror the trades of our top-performing traders and start earning from day one
            </p>
          </motion.div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column - Live Traders List */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-8"
            >
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
                  <div className="mb-4 sm:mb-0">
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">Top Copy Traders</h3>
                    <p className="text-gray-500 text-sm">Real-time performance • Updated live</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-600 font-semibold text-sm">LIVE</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {[
                    { 
                      name: 'Alexander Chen', 
                      username: '@alextrader', 
                      profit: '+324.7%', 
                      followers: '18.2K', 
                      winRate: '87%',
                      monthlyReturn: '+24.3%',
                      risk: 'Medium', 
                      avatar: 'AC',
                      status: 'trading',
                      lastTrade: '2m ago',
                      copiers: '2.1K'
                    },
                    { 
                      name: 'Sarah Williams', 
                      username: '@sarahfx', 
                      profit: '+198.5%', 
                      followers: '12.8K', 
                      winRate: '92%',
                      monthlyReturn: '+18.7%',
                      risk: 'Low', 
                      avatar: 'SW',
                      status: 'online',
                      lastTrade: '5m ago',
                      copiers: '1.8K'
                    },
                    { 
                      name: 'Marcus Rodriguez', 
                      username: '@cryptoking', 
                      profit: '+445.2%', 
                      followers: '24.7K', 
                      winRate: '78%',
                      monthlyReturn: '+31.2%',
                      risk: 'High', 
                      avatar: 'MR',
                      status: 'trading',
                      lastTrade: '1m ago',
                      copiers: '3.5K'
                    }
                  ].map((trader, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="group relative bg-gray-50 hover:bg-white border border-gray-100 hover:border-blue-200 rounded-xl p-4 lg:p-6 hover:shadow-md transition-all duration-300 cursor-pointer"
                    >
                      {/* Mobile-first layout */}
                      <div className="flex items-start space-x-4">
                        {/* Avatar */}
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold text-sm lg:text-lg">
                            {trader.avatar}
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">✓</span>
                          </div>
                          {/* Status indicator */}
                          <div className="absolute -top-1 -right-1">
                            <div className={`w-3 h-3 rounded-full ${
                              trader.status === 'trading' ? 'bg-green-500 animate-pulse' : 'bg-blue-500'
                            }`}></div>
                          </div>
                        </div>

                        {/* Trader Info */}
                        <div className="flex-1 min-w-0">
                          {/* Header row */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-gray-900 font-bold text-base lg:text-lg truncate">{trader.name}</h4>
                              <p className="text-gray-500 text-sm">{trader.username}</p>
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-green-600 font-bold text-lg lg:text-xl">{trader.profit}</div>
                              <div className="text-gray-400 text-xs">Total Return</div>
                            </div>
                          </div>

                          {/* Stats Grid - Responsive */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-4">
                            <div className="text-center">
                              <div className="text-gray-900 font-semibold text-sm">{trader.winRate}</div>
                              <div className="text-gray-400 text-xs">Win Rate</div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-900 font-semibold text-sm">{trader.monthlyReturn}</div>
                              <div className="text-gray-400 text-xs">Monthly</div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-900 font-semibold text-sm">{trader.followers}</div>
                              <div className="text-gray-400 text-xs">Followers</div>
                            </div>
                            <div className="text-center">
                              <div className="text-gray-900 font-semibold text-sm">{trader.copiers}</div>
                              <div className="text-gray-400 text-xs">Copiers</div>
                            </div>
                          </div>

                          {/* Bottom Row - Mobile optimized */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center space-x-3">
                              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                trader.risk === 'Low' ? 'bg-green-100 text-green-700' :
                                trader.risk === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {trader.risk} Risk
                              </div>
                              <span className="text-gray-400 text-xs">Last: {trader.lastTrade}</span>
                            </div>
                            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors duration-200 w-full sm:w-auto">
                              Copy Trader
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* View All Button */}
                <div className="mt-8 text-center">
                  <button className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl border border-gray-200 transition-all duration-300">
                    View All Traders
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Benefits & Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-4 space-y-6"
            >
              {/* Benefits Card */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-6">Why Copy Trade?</h4>
                <div className="space-y-4">
                  {[
                    { icon: '🚀', title: 'Instant Results', desc: 'Start earning from day one' },
                    { icon: '⚡', title: 'Real-time Copying', desc: 'Trades executed instantly' },
                    { icon: '🛡️', title: 'Risk Control', desc: 'Set your own limits' },
                    { icon: '💎', title: 'Verified Traders', desc: 'All traders are verified' }
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="text-xl">{benefit.icon}</div>
                      <div>
                        <h5 className="text-gray-900 font-semibold text-sm mb-1">{benefit.title}</h5>
                        <p className="text-gray-500 text-xs">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats Card */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-6">Platform Stats</h4>
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-2xl lg:text-3xl font-bold text-blue-600 mb-1">$47.2M+</div>
                    <div className="text-gray-600 text-sm">Assets Under Management</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">156</div>
                      <div className="text-gray-500 text-xs">Active Traders</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">89.3%</div>
                      <div className="text-gray-500 text-xs">Avg Win Rate</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-gray-900">24.7K</div>
                      <div className="text-gray-500 text-xs">Active Copiers</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-gray-900">+187%</div>
                      <div className="text-gray-500 text-xs">Avg Return</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <Link
                  to="/copy-trading"
                  className="block w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-center rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Start Copy Trading
                </Link>
                <Link
                  to="/copy-trading"
                  className="block w-full px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-center rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-300"
                >
                  Browse All Traders
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Bottom Features - Clean grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-16"
          >
            {[
              {
                icon: '📊',
                title: 'Advanced Analytics',
                description: 'Detailed performance metrics and risk analysis for every trader'
              },
              {
                icon: '🔒',
                title: 'Secure & Transparent',
                description: 'All trades are verified and executed through secure protocols'
              },
              {
                icon: '💰',
                title: 'Flexible Investment',
                description: 'Start with as little as $100 and scale up as you grow'
              }
            ].map((feature, index) => (
              <div key={index} className="text-center p-6 bg-white border border-gray-200 rounded-2xl hover:shadow-md transition-all duration-300">
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Live Market Data Section - Elegant & Mobile-First Design */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 lg:mb-16"
          >
            <div className="inline-flex items-center px-3 py-1.5 bg-green-50 border border-green-200 rounded-full mb-4">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
              <span className="text-green-700 text-sm font-medium">LIVE MARKET DATA</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Real-Time Market Intelligence
            </h2>
            
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional-grade market data with institutional accuracy
            </p>
          </motion.div>

          {/* Market Data Tabs - Mobile Optimized */}
          <div className="flex justify-center mb-8 lg:mb-12">
            <div className="bg-gray-100 rounded-xl p-1 w-full max-w-md lg:max-w-lg">
              <div className="grid grid-cols-3 gap-1">
                <button
                  onClick={() => setActiveMarketTab('crypto')}
                  className={`px-3 py-2.5 lg:px-6 lg:py-3 rounded-lg font-medium text-sm lg:text-base transition-all duration-300 ${
                    activeMarketTab === 'crypto'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Crypto
                </button>
                <button
                  onClick={() => setActiveMarketTab('stocks')}
                  className={`px-3 py-2.5 lg:px-6 lg:py-3 rounded-lg font-medium text-sm lg:text-base transition-all duration-300 ${
                    activeMarketTab === 'stocks'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Stocks
                </button>
                <button
                  onClick={() => setActiveMarketTab('indices')}
                  className={`px-3 py-2.5 lg:px-6 lg:py-3 rounded-lg font-medium text-sm lg:text-base transition-all duration-300 ${
                    activeMarketTab === 'indices'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Indices
                </button>
              </div>
            </div>
          </div>

          {/* Market Data Content */}
          <motion.div
            key={activeMarketTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {/* Main Market Data Display */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-100">
                  <div>
                    <h3 className="text-lg lg:text-xl font-bold text-gray-900">
                      {activeMarketTab === 'crypto' ? 'Top Cryptocurrencies' : 
                       activeMarketTab === 'stocks' ? 'Major US Stocks' : 'Market Indices'}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">Updated in real-time</p>
                  </div>
                  <div className="flex items-center space-x-2 bg-green-50 px-3 py-1.5 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-700 text-sm font-medium">Live</span>
                  </div>
                </div>

                {/* Market Data List - Mobile Optimized */}
                <div className="divide-y divide-gray-100">
                  {/* Crypto Data */}
                  {activeMarketTab === 'crypto' && liveMarketData.crypto.slice(0, 6).map((crypto, index) => (
                    <motion.div
                      key={crypto.symbol}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="p-4 lg:p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between">
                        {/* Asset Info */}
                        <div className="flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1">
                          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-sm lg:text-base">
                              {crypto.symbol.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center space-x-2">
                              <h4 className="font-bold text-gray-900 text-sm lg:text-base">
                                {crypto.symbol}
                              </h4>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                #{crypto.rank || index + 1}
                              </span>
                            </div>
                            <p className="text-gray-500 text-xs lg:text-sm truncate">
                              {crypto.name}
                            </p>
                          </div>
                        </div>

                        {/* Price & Change */}
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="font-bold text-gray-900 text-sm lg:text-lg">
                            ${typeof crypto.price === 'number' 
                              ? crypto.price < 1 
                                ? crypto.price.toFixed(6)
                                : crypto.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : crypto.price}
                          </div>
                          <div className={`text-xs lg:text-sm font-medium ${
                            crypto.positive ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {crypto.positive ? '↗' : '↘'} {Math.abs(parseFloat(crypto.change) || 0).toFixed(2)}%
                          </div>
                        </div>

                        {/* Volume - Hidden on mobile */}
                        <div className="hidden md:block text-right ml-6 min-w-0">
                          <div className="text-xs text-gray-500">24h Volume</div>
                          <div className="text-sm font-medium text-gray-700">
                            ${marketDataService.formatNumber(crypto.volume)}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Stock Data */}
                  {activeMarketTab === 'stocks' && liveMarketData.stocks.slice(0, 6).map((stock, index) => (
                    <motion.div
                      key={stock.symbol}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="p-4 lg:p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between">
                        {/* Asset Info */}
                        <div className="flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1">
                          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-xs lg:text-sm">
                              {stock.symbol}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-900 text-sm lg:text-base">
                              {stock.symbol}
                            </h4>
                            <p className="text-gray-500 text-xs lg:text-sm truncate">
                              {stock.name}
                            </p>
                          </div>
                        </div>

                        {/* Price & Change */}
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="font-bold text-gray-900 text-sm lg:text-lg">
                            ${parseFloat(stock.price).toFixed(2)}
                          </div>
                          <div className={`text-xs lg:text-sm font-medium ${
                            stock.positive ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {stock.positive ? '↗' : '↘'} ${Math.abs(parseFloat(stock.change) || 0).toFixed(2)}
                          </div>
                        </div>

                        {/* Volume - Hidden on mobile */}
                        <div className="hidden md:block text-right ml-6 min-w-0">
                          <div className="text-xs text-gray-500">Volume</div>
                          <div className="text-sm font-medium text-gray-700">
                            {stock.volume}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Index Data */}
                  {activeMarketTab === 'indices' && liveMarketData.indices.map((index, idx) => (
                    <motion.div
                      key={index.symbol}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      className="p-4 lg:p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between">
                        {/* Asset Info */}
                        <div className="flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1">
                          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-xs">
                              {index.symbol.slice(0, 3)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-900 text-sm lg:text-base">
                              {index.symbol}
                            </h4>
                            <p className="text-gray-500 text-xs lg:text-sm truncate">
                              {index.name}
                            </p>
                          </div>
                        </div>

                        {/* Price & Change */}
                        <div className="text-right flex-shrink-0 ml-4">
                          <div className="font-bold text-gray-900 text-sm lg:text-lg">
                            {index.price}
                          </div>
                          <div className={`text-xs lg:text-sm font-medium ${
                            index.positive ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {index.positive ? '↗' : '↘'} {index.change}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Market Stats Sidebar */}
            <div className="space-y-6">
              {/* Market Status */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
              >
                <h4 className="text-lg font-bold text-gray-900 mb-4">Market Status</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">US Markets</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-green-600 font-medium text-sm">Open</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Crypto</span>
                    <div className="flex items-center space-x-1.5">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-green-600 font-medium text-sm">24/7</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Updates</span>
                    <span className="text-gray-900 font-medium text-sm">Real-time</span>
                  </div>
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
              >
                <h4 className="text-lg font-bold text-gray-900 mb-4">Market Overview</h4>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Fear & Greed</span>
                    <span className="text-orange-600 font-bold text-sm">72 (Greed)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">BTC Dominance</span>
                    <span className="text-gray-900 font-bold text-sm">54.2%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Total Market Cap</span>
                    <span className="text-gray-900 font-bold text-sm">$2.89T</span>
                  </div>
                </div>
              </motion.div>

              {/* Quick Actions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
              >
                <h4 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h4>
                <div className="space-y-3">
                  <Link
                    to="/trading-platform"
                    className="flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200 group"
                  >
                    <span className="text-blue-700 font-medium text-sm">Start Trading</span>
                    <ArrowRightIcon className="h-4 w-4 text-blue-600 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                  
                  <Link
                    to="/markets"
                    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-all duration-200 group"
                  >
                    <span className="text-gray-700 font-medium text-sm">All Markets</span>
                    <ArrowRightIcon className="h-4 w-4 text-gray-600 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                  
                  <Link
                    to="/news"
                    className="flex items-center justify-between p-3 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-200 group"
                  >
                    <span className="text-green-700 font-medium text-sm">Research</span>
                    <ArrowRightIcon className="h-4 w-4 text-green-600 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Professional Analytics Section */}
      <section className="py-24 bg-gray-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-blue-500/10 border border-blue-400/20 rounded-full">
                  <span className="text-blue-300 text-sm font-medium">Advanced Analytics</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                  Professional Market
                  <span className="block text-blue-400">Research Tools</span>
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Access institutional-grade research and analytics powered by real-time data 
                  and advanced charting tools used by professional traders worldwide.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Real-Time Data</h4>
                    <p className="text-gray-300">Live market data with advanced technical indicators and pattern recognition.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">AI Insights</h4>
                    <p className="text-gray-300">Machine learning algorithms provide actionable trading signals and market forecasts.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Risk Management</h4>
                    <p className="text-gray-300">Comprehensive portfolio analysis with intelligent risk assessment tools.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/news"
                  className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Explore Research Tools
                  <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={analystTeamImage}
                  alt="Professional analysts analyzing financial data"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="text-center">
                    <div className="text-lg font-bold text-gray-900">94.7%</div>
                    <div className="text-xs text-gray-600">Accuracy Rate</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Expert Team Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={teamImage}
                  alt="Professional trading team in monitoring room"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-semibold text-gray-900">Live Trading Floor</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-green-50 border border-green-200 rounded-full">
                  <span className="text-green-700 text-sm font-medium">Expert Team</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                  World-Class Trading
                  <span className="block text-green-600">Professionals</span>
                </h2>
                
                <p className="text-xl text-gray-600 leading-relaxed">
                  Our team of seasoned professionals brings decades of Wall Street experience, 
                  providing you with institutional-grade support and market insights.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-blue-600 mb-2">25+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
                <div className="text-center p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">$2.5B+</div>
                  <div className="text-sm text-gray-600">Assets Managed</div>
                </div>
                <div className="text-center p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
                  <div className="text-sm text-gray-600">Market Monitoring</div>
                </div>
                <div className="text-center p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                  <div className="text-3xl font-bold text-orange-600 mb-2">50+</div>
                  <div className="text-sm text-gray-600">Countries Served</div>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/about"
                  className="inline-flex items-center px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Meet Our Team
                  <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Innovation Section */}
      <section className="py-24 bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-blue-500/10 border border-blue-400/20 rounded-full">
                  <span className="text-blue-300 text-sm font-medium">Next-Gen Technology</span>
                </div>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                  Cutting-Edge Trading
                  <span className="block text-blue-400">Infrastructure</span>
                </h2>
                
                <p className="text-xl text-gray-300 leading-relaxed">
                  Experience lightning-fast execution and enterprise-grade security with our 
                  state-of-the-art trading infrastructure built for the modern trader.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Lightning Speed</h4>
                    <p className="text-gray-400">Sub-millisecond execution with advanced order routing technology.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Bank-Level Security</h4>
                    <p className="text-gray-400">Military-grade encryption and multi-layer security protocols.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2">Global Infrastructure</h4>
                    <p className="text-gray-400">Worldwide server network ensuring 99.99% uptime guarantee.</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Link
                  to="/trading-platform"
                  className="inline-flex items-center px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Experience Platform
                  <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={heroImage}
                  alt="Futuristic trading technology environment"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute top-6 right-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">&lt;1ms</div>
                    <div className="text-xs text-gray-300">Latency</div>
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-400">99.99%</div>
                    <div className="text-xs text-gray-300">Uptime</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 